import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AuthContext } from "../../context/AuthContext";
import {
  Home,
  Building2,
  Search,
  ShieldCheck,
  ArrowRight,
  MapPin,
  Users,
  Award,
  Target,
} from "lucide-react";

export default function AboutPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const handleSearch = () => {
    navigate("/search");
  };

  const handleAddListing = () => {
    // Not logged in
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent("/add-listing")}`);
      return;
    }

    // Only OWNER or ADMIN
    if (!["OWNER", "ADMIN"].includes(user.role)) {
      toast.error("Only Property Owners and Administrators can list properties.");
      return;
    }

    navigate("/add-listing");
  };

  return (
    <div className="bg-white">
      {/* ================================================= */}
      {/* HERO SECTION */}
      {/* ================================================= */}

      <section className="relative overflow-hidden bg-gradient-to-br from-orange-500 via-orange-600 to-red-500">
        {/* Background Decorations */}

        <div className="absolute inset-0">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl"></div>

          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-yellow-300/10 blur-3xl"></div>

          <div className="absolute top-1/2 left-1/2 w-72 h-72 rounded-full bg-white/5 blur-2xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-4 lg:py-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            {/* LEFT CONTENT */}

            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-5 py-2 text-white font-medium mb-5">
                <Home size={18} />
                Nepal's Trusted Rental Marketplace
              </div>

              <h1 className="text-5xl lg:text-5xl font-bold leading-tight text-white">
                Welcome to
                <span className="block text-yellow-300">RoomVetiyo</span>
              </h1>

              <p className="mt-6 text-xl leading-8 text-orange-100 max-w-xl">
                RoomVetiyo is Nepal's dedicated room rental platform, helping
                tenants discover their perfect place while empowering property
                owners to connect with genuine renters quickly, safely, and
                conveniently.
              </p>

              <div className="flex flex-wrap gap-5 mt-6">
                <button
                  onClick={handleSearch}
                  className="bg-white text-orange-600 px-8 py-4 rounded-2xl font-semibold flex items-center gap-2 hover:scale-105 transition"
                >
                  Explore Rooms
                  <ArrowRight size={18} />
                </button>

                <button
                  onClick={handleAddListing}
                  className="border border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-orange-600 transition"
                >
                  List Your Property
                </button>
              </div>
            </div>

            {/* RIGHT CONTENT */}

            <div className="relative">
              <div className="bg-white rounded-[40px] shadow-2xl p-3">
                <img
                  src="/hero.jpeg"
                  alt="RoomVetiyo"
                  className="rounded-3xl h-[275px] w-full object-cover"
                />

                <div className="grid grid-cols-2 gap-5 mt-2">
                  <div className="bg-orange-50 rounded-2xl p-2">
                    <p className="text-gray-500 text-sm">Verified Listings</p>

                    <h3 className="text-3xl font-bold text-orange-600 mt-1">
                      100%
                    </h3>
                  </div>

                  <div className="bg-green-50 rounded-2xl p-2">
                    <p className="text-gray-500 text-sm">Happy Customers</p>

                    <h3 className="text-3xl font-bold text-green-600 mt-1">
                      10K+
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* COMPANY STATISTICS */}
      {/* ================================================= */}

      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-3xl p-6 text-center shadow-sm hover:shadow-lg transition">
              <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto">
                <Building2 className="text-orange-600" />
              </div>

              <h2 className="text-4xl font-bold mt-5">1000+</h2>

              <p className="text-gray-500 mt-2">Property Listings</p>
            </div>

            <div className="bg-white rounded-3xl p-6 text-center shadow-sm hover:shadow-lg transition">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto">
                <Users className="text-blue-600" />
              </div>

              <h2 className="text-4xl font-bold mt-5">5000+</h2>

              <p className="text-gray-500 mt-2">Active Users</p>
            </div>

            <div className="bg-white rounded-3xl p-6 text-center shadow-sm hover:shadow-lg transition">
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto">
                <MapPin className="text-green-600" />
              </div>

              <h2 className="text-4xl font-bold mt-5">77</h2>

              <p className="text-gray-500 mt-2">District Coverage</p>
            </div>

            <div className="bg-white rounded-3xl p-6 text-center shadow-sm hover:shadow-lg transition">
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto">
                <Award className="text-purple-600" />
              </div>

              <h2 className="text-4xl font-bold mt-5">24/7</h2>

              <p className="text-gray-500 mt-2">Customer Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* ABOUT ROOMVETIYO */}
      {/* ================================================= */}

      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* LEFT IMAGES */}

            <div className="relative">
              <img
                src="/easy_search.jpeg"
                alt="RoomVetiyo"
                className="rounded-[35px] shadow-xl h-[500px] w-full object-cover"
              />

              <div className="absolute top-8 left-8 bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <Search className="text-orange-600" />
                  </div>

                  <div>
                    <h3 className="font-bold text-2xl">Easy Search</h3>

                    <p className="text-gray-500">Find your perfect room</p>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT CONTENT */}

            <div>
              <span className="text-orange-600 uppercase tracking-widest font-semibold">
                About RoomVetiyo
              </span>

              <h2 className="text-5xl font-bold mt-1 leading-tight">
                Nepal's Modern Rental Marketplace
              </h2>

              <p className="mt-4 text-lg text-gray-600 leading-7">
                RoomVetiyo is Nepal's dedicated rental platform created to
                simplify the process of finding and listing rental properties.
                Whether you're searching for a room, apartment, hostel, office
                space, commercial property or house, our platform connects
                tenants directly with genuine property owners.
              </p>

              <p className="mt-4 text-lg text-gray-600 leading-7">
                We believe finding a rental property shouldn't be stressful.
                That's why we've built a simple, transparent and reliable
                platform where people can search, compare and connect with
                confidence.
              </p>

              <div className="grid grid-cols-2 gap-6 mt-5">
                <div className="flex gap-4">
                  <div className="w-14 h-14 rounded-xl bg-orange-100 flex items-center justify-center">
                    <ShieldCheck className="text-orange-600" />
                  </div>

                  <div>
                    <h4 className="font-bold">Trusted Platform</h4>

                    <p className="text-gray-500 text-sm mt-2">
                      Secure and verified listings.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center">
                    <Users className="text-green-600" />
                  </div>

                  <div>
                    <h4 className="font-bold">Genuine Users</h4>

                    <p className="text-gray-500 text-sm mt-2">
                      Owners and seekers together.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* MISSION & VISION */}
      {/* ================================================= */}

      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Our Purpose
            </span>

            <h2 className="text-5xl font-bold mt-2">Mission & Vision</h2>

            <p className="text-lg text-gray-600 mt-3 leading-9">
              Everything we build is focused on making renting easier, faster
              and more transparent for everyone across Nepal.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 mt-6">
            {/* Mission */}

            <div className="bg-white rounded-[35px] px-10 py-3 shadow-sm hover:shadow-xl transition">
              <div className="w-16 h-16 rounded-3xl bg-orange-100 flex items-center justify-center">
                <Target size={40} className="text-orange-600" />
              </div>

              <h3 className="text-3xl font-bold mt-4">Our Mission</h3>

              <p className="mt-3 text-gray-600 leading-7">
                To simplify the rental experience in Nepal by providing a
                trusted, transparent and user-friendly platform that benefits
                both property owners and room seekers.
              </p>

              <div className="mt-5 space-y-2">
                <div className="flex gap-3">
                  <span className="text-green-600">✔</span>
                  Trusted Listings
                </div>

                <div className="flex gap-3">
                  <span className="text-green-600">✔</span>
                  Easy Communication
                </div>

                <div className="flex gap-3">
                  <span className="text-green-600">✔</span>
                  Faster Property Search
                </div>

                <div className="flex gap-3">
                  <span className="text-green-600">✔</span>
                  Better Rental Experience
                </div>
              </div>
            </div>

            {/* Vision */}

            <div className="bg-orange-500 rounded-[35px] px-10 py-3 text-white shadow-xl">
              <div className="w-16 h-16 rounded-3xl bg-white/20 flex items-center justify-center">
                <Award size={40} className="text-white" />
              </div>

              <h3 className="text-3xl font-bold mt-4">Our Vision</h3>

              <p className="mt-3 leading-7 text-orange-100">
                To become Nepal's most trusted online rental marketplace, making
                room searching and property listing simple, accessible and
                reliable for every citizen.
              </p>

              <div className="mt-4 bg-white/10 rounded-3xl px-6 py-3">
                <h4 className="font-semibold">Our Goal</h4>

                <p className="mt-2 text-orange-100 leading-8">
                  Build the largest, smartest and most reliable rental ecosystem
                  in Nepal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* WHAT WE OFFER */}
      {/* ================================================= */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Our Services
            </span>

            <h2 className="text-5xl font-bold mt-4">What We Offer</h2>

            <p className="mt-4 text-lg text-gray-600 leading-7">
              Whether you're a student, working professional, family or business
              owner, RoomVetiyo helps you discover the perfect rental property
              across Nepal.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-6">
            {[
              {
                icon: "🏠",
                title: "Rooms for Rent",
                desc: "Affordable rooms for students, professionals and families.",
              },
              {
                icon: "🏢",
                title: "Flats & Apartments",
                desc: "Modern apartments and flats in prime locations.",
              },
              {
                icon: "🏡",
                title: "Houses",
                desc: "Independent houses for individuals and families.",
              },
              {
                icon: "🏫",
                title: "Hostels",
                desc: "Verified hostel listings near schools and colleges.",
              },
              {
                icon: "💼",
                title: "Office Spaces",
                desc: "Commercial offices for startups and businesses.",
              },
              {
                icon: "🍽️",
                title: "Commercial Spaces",
                desc: "Restaurants, shops and business properties.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-[30px] px-8 py-4 shadow-sm hover:shadow-xl transition duration-300 hover:-translate-y-2"
              >
                <div className="text-5xl">{item.icon}</div>

                <h3 className="text-2xl font-bold mt-6">{item.title}</h3>

                <p className="mt-5 text-gray-600 leading-7">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* PROPERTY TYPES */}
      {/* ================================================= */}
      <section className="py-8 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-5xl font-bold">Property Types</h2>

            <p className="mt-3 text-xl text-orange-100">
              Thousands of rental opportunities available throughout Nepal.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mt-8">
            {[
              "🏠 Room",
              "🏢 Flat",
              "🏡 House",
              "🏫 Hostel",
              "🏬 Office",
              "🍽️ Commercial",
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white/10 rounded-3xl px-8 py-6 backdrop-blur text-center hover:bg-white/20 transition"
              >
                <div className="text-5xl">{item.split(" ")[0]}</div>

                <h4 className="font-semibold mt-3">
                  {item.split(" ").slice(1).join(" ")}
                </h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* WHY CHOOSE ROOMVETIYO */}
      {/* ================================================= */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Why Choose Us
            </span>

            <h2 className="text-5xl font-bold mt-3">
              Why Thousands Choose RoomVetiyo
            </h2>

            <p className="mt-4 text-lg text-gray-600 leading-7">
              We are committed to making property searching simple, transparent
              and stress-free for everyone.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mt-6">
            {[
              {
                icon: "⚡",
                title: "Fast Search",
                desc: "Find rental properties quickly using powerful filters and location-based search.",
              },
              {
                icon: "🔒",
                title: "Trusted Platform",
                desc: "Verified listings and secure user accounts help build confidence.",
              },
              {
                icon: "📱",
                title: "Easy to Use",
                desc: "A clean and modern interface designed for everyone.",
              },
              {
                icon: "💰",
                title: "Affordable Listing",
                desc: "Property owners can reach thousands of tenants at an affordable cost.",
              },
              {
                icon: "💬",
                title: "Direct Communication",
                desc: "Chat directly with property owners without unnecessary middlemen.",
              },
              {
                icon: "📍",
                title: "Growing Across Nepal",
                desc: "More cities, more listings and more opportunities every day.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="group bg-gray-50 rounded-[30px] px-8 py-4 hover:bg-orange-500 hover:text-white transition-all duration-300"
              >
                <div className="text-5xl">{item.icon}</div>

                <h3 className="text-2xl font-bold mt-6">{item.title}</h3>

                <p className="mt-4 leading-8 text-gray-600 group-hover:text-orange-100">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* CALL TO ACTION */}
      {/* ================================================= */}

      <section className="py-8 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold">
            Ready to Find Your Perfect Place?
          </h2>

          <p className="mt-4 text-xl leading-7 text-orange-100">
            Join thousands of tenants and property owners using RoomVetiyo to
            make renting easier, faster and more transparent.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <button className="bg-white text-orange-600 px-10 py-2 rounded-2xl font-semibold hover:scale-105 transition">
              Find Rooms
            </button>

            <button className="border border-white px-10 py-2 rounded-2xl font-semibold hover:bg-white hover:text-orange-600 transition">
              List Property
            </button>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* CONTACT */}
      {/* ================================================= */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Contact Us
            </span>
            <h2 className="text-5xl font-bold mt-3">
              We'd Love To Hear From You
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-8 mt-6">
            <div className="bg-orange-50 rounded-[30px] px-10 py-4 text-center">
              <div className="text-5xl">🌐</div>
              <h3 className="font-bold text-2xl mt-3">Website</h3>
              <p className="mt-4 text-gray-600">www.roomvetiyo.com</p>
            </div>
            <div className="bg-blue-50 rounded-[30px] px-10 py-4 text-center">
              <div className="text-5xl">📧</div>
              <h3 className="font-bold text-2xl mt-3">Email</h3>
              <p className="mt-4 text-gray-600">roomvetiyo@gmail.com</p>
            </div>
            <div className="bg-green-50 rounded-[30px] px-10 py-4 text-center">
              <div className="text-5xl">📞</div>
              <h3 className="font-bold text-2xl mt-3">Phone</h3>
              <p className="mt-4 text-gray-600">+977 970-8919809</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* FAQ */}
      {/* ================================================= */}

      <section className="py-8 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Frequently Asked Questions
            </span>
            <h2 className="text-5xl font-bold mt-3">Have Questions?</h2>
          </div>
          <div className="space-y-4 mt-4">
            {[
              {
                q: "Is RoomVetiyo free to use?",
                a: "Yes. Searching rental properties is completely free.",
              },
              {
                q: "Can property owners list their rooms?",
                a: "Yes. Owners can easily create and manage property listings.",
              },
              {
                q: "How do I contact a property owner?",
                a: "Use our built-in messaging system to communicate directly.",
              },
              {
                q: "Are listings verified?",
                a: "We encourage verified listings and continuously review reported content.",
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl px-8 py-3 shadow-sm"
              >
                <h3 className="font-bold text-xl">{faq.q}</h3>
                <p className="text-gray-600 mt-1 leading-7">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* FINAL CALL TO ACTION */}
      {/* ================================================= */}

      <section className="relative overflow-hidden bg-gradient-to-r from-orange-600 via-orange-500 to-red-500 py-8">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-white blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-yellow-300 blur-3xl"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <span className="inline-block px-6 py-2 rounded-full bg-white/20 text-white font-semibold mb-4">
            Join Nepal's Growing Rental Community
          </span>
          <h2 className="text-5xl lg:text-6xl font-bold text-white">
            Ready to Find Your
            <span className="block text-yellow-300 mt-3">Perfect Rental?</span>
          </h2>
          <p className="max-w-3xl mx-auto mt-3 text-xl text-orange-100 leading-7">
            Whether you're looking for your next home, searching for office
            space, or listing your property, RoomVetiyo is here to make the
            process simple, transparent and secure.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mt-4">
            <button
              onClick={() => (window.location.href = "/search")}
              className="px-10 py-4 bg-white text-orange-600 rounded-2xl font-bold text-lg hover:scale-105 transition duration-300"
            >
              Explore Properties
            </button>
            <button
              onClick={() => (window.location.href = "/add-listing")}
              className="px-10 py-4 border-2 border-white text-white rounded-2xl font-bold text-lg hover:bg-white hover:text-orange-600 transition duration-300"
            >
              List Your Property
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
